import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketDataService } from '../../services/marketDataService';
import { usePermissions } from '../../hooks/usePermissions';
import { UpgradeLink } from '../UpgradeLink';
import { OverviewChart } from './OverviewChart';
import { ContractLeaders } from './ContractLeaders';
import { TopContractsPanel } from './TopContractsPanel';
import {
  useContractLeaders,
  useSymbolHeader,
  useTickerStats,
  useTopContracts,
} from './contractMetrics';
import {
  GammaLevelsColumn,
  PriceStatsColumn,
  VolumeColumn,
  FlowStatsColumn,
} from './OverviewStats';
import { fmtUsDate, fmtAlertTime, fmtAlertDateTime, fmtRefPrice, fmtPremium } from './format';
import type { OptionTrade } from '../../services/optionTradesService';
import { fznService } from '../../services/fznService';
import { etToday, etCalendarDate } from '../wiseguy/alertStore';
import { toRecentRows, toLargestRows } from './alertFlow';
import { isTopContractsUnavailable } from '../../services/topContractsService';
import { EXPLORER_CACHE } from './cachePolicy';

interface Props { symbol: string; }

/* ── shared chrome ──────────────────────────────────────────────────── */

function Panel({ title, meta, children }: {
  title: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  // ⚠️ The background is load-bearing, exactly as in OverviewStats' Column.
  // SplitCard draws its dividers by showing a border-coloured background
  // through a 1px gap, so a child without an opaque surface of its own does
  // not get a hairline — it gets the border colour behind the whole panel.
  // Harmless inside a plain Card, which paints the same surface anyway.
  return (
    <section className="p-4 bg-dark-surface">
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">{title}</h2>
        {meta}
      </div>
      {children}
    </section>
  );
}

/**
 * One section of the page.
 *
 * The unit is the section, not the module: panels that are views of the same
 * thing share a card and are divided by hairlines, while genuinely different
 * measurements get cards of their own with a gap between. Same rule as Market
 * Overview — see docs/FUZION_UI_HANDOFF.md section 9b.
 */
function Card({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="rounded-lg border border-dark-border bg-dark-surface overflow-hidden">
      {children}
    </div>
  );
}

/**
 * A card whose children are separated by 1px of border colour.
 *
 * The gap draws the rules rather than divide-*, which cannot: which edges need
 * a line changes when the columns rewrap. Children must carry their own
 * bg-dark-surface or the border colour shows through.
 */
function SplitCard({ className, children }: {
  className: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={`rounded-lg border border-dark-border bg-dark-border overflow-hidden grid gap-px ${className}`}>
      {children}
    </div>
  );
}

/**
 * Shown in place of a gated block instead of an error.
 *
 * These endpoints 403 for users without the tier, and a red "failed to load"
 * would read as a bug rather than a paywall.
 */
function Locked({ what }: { what: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
      <svg className="w-4 h-4 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="4" y="11" width="16" height="9" rx="2" />
        <path d="M8 11V7a4 4 0 018 0v4" strokeLinecap="round" />
      </svg>
      <span>{what} requires a Fuzion subscription.</span>
      <UpgradeLink />
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="text-gray-400 text-sm">{children}</p>;
}

/* ── flow panels ──────────────────────────────────────────── */

const FLOW_ROWS = 8;

/**
 * Rows requested from the alert feed.
 *
 * ⚠️ **Deliberately far larger than `FLOW_ROWS`.** Largest Alerts ranks
 * client-side, so it needs the session rather than a page — requesting eight
 * and sorting them would rank only the eight most recent. Matches
 * `SESSION_LIMIT` in `useWiseguyAlerts`.
 */
const ALERT_SESSION_LIMIT = 1000;

/**
 * Trailing days requested from the alert feed.
 *
 * ⚠️ **An explicit `from`/`to` window, not the dateless "last session" mode.**
 * Omitting the dates on the ticker-scoped route is a path nothing else
 * exercises — every working caller (`fetchTickerSearch` in `useWiseguyAlerts`)
 * sends a window. Sending one keeps this on the same path as the Wiseguy tool,
 * which is known good.
 *
 * ⚠️ **Must be a pair — a lone `from` 422s.**
 *
 * Ten sessions, so a ticker that alerts intermittently still fills the panels
 * rather than showing an empty table on any day it happened to be quiet. This
 * is a calendar-day window, so weekends and holidays consume days from it — ten
 * covers roughly two trading weeks.
 *
 * ⚠️ **The rows therefore span sessions, and both tables show a date column.**
 * Without it two rows from different days are indistinguishable, and "Largest"
 * would silently compare across sessions with no way to see it.
 */
const ALERT_LOOKBACK_DAYS = 10;

const TH = 'px-3 py-2 font-semibold text-gray-400 text-center whitespace-nowrap';
const TD = 'px-3 py-1.5 whitespace-nowrap text-center';

/**
 * The tape table shared by both flow panels.
 *
 * Alignment, badges and cell padding mirror the Wiseguy Alerts tool. The Symbol
 * column is dropped — every row here is the ticker already named at the top of
 * the page.
 *
 * ⚠️ **No Mult column.** The old table rendered a contract multiplier from the
 * Wiseguy alert. `/v1/option-trades` does not carry one, and it must not be
 * hardcoded to ×100: the assumption is wrong after any split-adjusted or
 * non-standard deliverable, and it would be wrong invisibly.
 *
 * Nine columns is wide for a half-width panel, so it scrolls rather than
 * wrapping. Dropping columns to fit would put the same print on screen with
 * different fields depending on which panel it appeared in.
 */
function FlowTable({ trades, withDate = false }: {
  trades: OptionTrade[];
  /** Show the date on every row. Required whenever rows span sessions. */
  withDate?: boolean;
}): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded border border-dark-border">
      <table className="w-full text-xs">
        <thead className="bg-dark-surface-light border-b border-dark-border">
          <tr>
            <th className={TH}>Time</th>
            <th className={TH}>Bias</th>
            <th className={TH}>Type</th>
            <th className={TH}>Ref Price</th>
            <th className={TH}>Strike</th>
            <th className={TH}>Size</th>
            <th className={TH}>Expiry</th>
            <th className={TH}>DTE</th>
            <th className={TH}>Condition</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <tr
              key={t.id}
              className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
            >
              <td className={`${TD} text-gray-400`}>
                {withDate ? fmtAlertDateTime(t.ts) : fmtAlertTime(t.ts)}
              </td>
              {/* ⚠️ Read from `bias`, never derived from `option_type` or
                  `side`. A put bought at the ask is side=ask and bias=bearish;
                  inferring direction from either field alone paints aggressive
                  short prints bullish. The old table fell back to
                  "Call ⇒ Bullish" when bias was blank — that fallback is gone
                  deliberately, because this feed always sends bias. */}
              <td
                className={`${TD} font-medium capitalize ${
                  t.bias === 'bullish'
                    ? 'text-bull-blue'
                    : t.bias === 'bearish'
                      ? 'text-bear-red'
                      : 'text-gray-400'
                }`}
              >
                {t.bias}
              </td>
              <td className={TD}>
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] ${
                    t.option_type === 'Call'
                      ? 'bg-bull-blue/20 text-bull-blue'
                      : 'bg-bear-red/20 text-bear-red'
                  }`}
                >
                  {t.option_type}
                </span>
              </td>
              {/* Spot first, then the strike read against it. */}
              <td className={`${TD} font-numeric text-gray-300`}>
                {fmtRefPrice(t.underlying_price)}
              </td>
              <td className={`${TD} font-numeric text-white`}>{t.strike.toFixed(2)}</td>
              {/* Dollars, not contracts. Null on sort=time renders as a dash —
                  see fmtPremium. */}
              <td className={`${TD} font-numeric text-white`}>{fmtPremium(t.premium)}</td>
              <td className={`${TD} font-numeric text-white`}>{fmtUsDate(t.expiry)}</td>
              <td className={`${TD} font-numeric text-white`}>{`${t.dte}d`}</td>
              <td className={`${TD} text-gray-400`}>{t.condition || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Shared chrome for the two flow panels: gating, loading, error, empty. */
function FlowPanel({ title, meta, hasAccess, isLoading, isError, trades, empty, withDate }: {
  title: string;
  meta?: React.ReactNode;
  hasAccess: boolean;
  isLoading: boolean;
  isError: boolean;
  trades: OptionTrade[];
  empty: string;
  withDate?: boolean;
}): React.ReactElement {
  return (
    <Panel title={title} meta={hasAccess ? meta : undefined}>
      {!hasAccess ? (
        <Locked what="Options flow" />
      ) : isLoading ? (
        <div className="animate-pulse space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-5 bg-dark-border rounded" />)}
        </div>
      ) : isError ? (
        <p className="text-bear-red text-sm">Failed to load flow</p>
      ) : !trades.length ? (
        // ⚠️ An empty `trades` array is a valid 200 — a closed session, an
        // illiquid name, or a degraded upstream. Never an error state.
        <Empty>{empty}</Empty>
      ) : (
        <FlowTable trades={trades} withDate={withDate} />
      )}
    </Panel>
  );
}

/**
 * The session's FZN alerts for this ticker, shared by both flow panels.
 *
 * ⚠️ **One request, two views.** Both panels read the same endpoint for the
 * same symbol and session; they differ only in ordering. Same pattern as the
 * three leaderboards below, which are three views of one `/v1/contract-leaders`
 * response.
 *
 * ⚠️ **An explicit `from`/`to` window is sent.** The dateless "resolve the last
 * session" mode is documented on this route but exercised by nothing else, and
 * the panels came up empty on active tickers without it. Every known-good
 * caller sends a window; see `ALERT_LOOKBACK_DAYS`.
 *
 * ⚠️ **The whole window is fetched, not a page.** Largest Alerts ranks
 * client-side because the alert route has no `sort` parameter, and a ranking is
 * only honest over everything it claims to rank — see `toLargestRows`.
 */
function useTickerAlertFlow(symbol: string, hasAccess: boolean) {
  return useQuery({
    queryKey: ['tickerOverviewAlertFlow', symbol],
    queryFn: () =>
      fznService.getTickerAlerts(symbol, {
        from: etCalendarDate(ALERT_LOOKBACK_DAYS - 1),
        to: etToday(),
        limit: ALERT_SESSION_LIMIT,
      }),
    ...EXPLORER_CACHE,
    enabled: !!symbol && hasAccess,
  });
}

/**
 * Window caption for the alert panels.
 *
 * ⚠️ **Names the window, not a session.** The rows span
 * `ALERT_LOOKBACK_DAYS`, so captioning them with the response's single
 * `session_date` would date a multi-session table to one day — every row from
 * an earlier session silently mis-attributed. Each row carries its own date
 * instead; this only says how far back the panel reaches.
 */
function AlertMeta(): React.ReactElement {
  return (
    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-numeric">
      Last {ALERT_LOOKBACK_DAYS} sessions
    </span>
  );
}

/**
 * The most recent FZN alerts on this ticker.
 *
 * ⚠️ **This is the alert feed, not the tape.** It previously read
 * `/v1/option-trades`, which carries every print. These rows are the floored,
 * curated alerts — the same set the Wiseguy Alerts tool shows — so expect far
 * fewer of them. The heading says "Alerts" for exactly that reason.
 *
 * ⚠️ **Rows span the whole window, not one session**, so each carries its date.
 *
 * ⚠️ **Every alert is rendered — sweeps included.** An earlier version filtered
 * on `n_legs > 1`, which is the fill count, not a strategy-leg count; that
 * removed every MULTI-SWEEP and left the panels empty on active tickers. See
 * `alertFlow.ts`.
 */
function RecentFlow({ symbol, hasAccess }: { symbol: string; hasAccess: boolean }): React.ReactElement {
  const { data, isLoading, isError } = useTickerAlertFlow(symbol, hasAccess);

  const trades = React.useMemo(
    () => toRecentRows(data?.data ?? []).slice(0, FLOW_ROWS),
    [data]
  );

  return (
    <FlowPanel
      title="Recent Alerts"
      meta={<AlertMeta />}
      hasAccess={hasAccess}
      isLoading={isLoading}
      isError={isError}
      trades={trades}
      empty={`No FZN alerts in the last ${ALERT_LOOKBACK_DAYS} sessions for ${symbol}`}
      withDate
    />
  );
}

/**
 * The largest FZN alerts on this ticker, by premium.
 *
 * ⚠️ **Ranked client-side, and that is not a regression.** The tape endpoint
 * ranked server-side and a local sort was correctly removed from this panel;
 * the alert route offers no `sort` parameter at all, so there is no server
 * ranking to prefer. `toLargestRows` ranks the full session before slicing —
 * ranking a short page would mean "largest of the most recent few" under a
 * heading saying "Largest".
 *
 * ⚠️ **No premium floor.** The tape needed `min_premium` to keep the long tail
 * out of the ranking; the alert feed is floored server-side already.
 *
 * ⚠️ **Rows stay in premium order rather than being restored to time order.**
 * The ranking IS the information: the top row is the session's largest alert.
 */
function TopFlowSessions({ symbol, hasAccess }: { symbol: string; hasAccess: boolean }): React.ReactElement {
  const { data, isLoading, isError } = useTickerAlertFlow(symbol, hasAccess);

  const trades = React.useMemo(
    () => toLargestRows(data?.data ?? [], FLOW_ROWS),
    [data]
  );

  // ⚠️ QuantForge caps a response at 5000 rows. If that fires, the client sort
  // is ranking a partial session and this panel may be missing the true
  // largest — surfaced rather than failing silently.
  React.useEffect(() => {
    if (data?.truncated) {
      console.warn(
        `[TickerOverview] Alert history truncated for ${symbol}; Largest Alerts may be incomplete.`
      );
    }
  }, [data?.truncated, symbol]);

  return (
    <FlowPanel
      title="Largest Alerts"
      meta={<AlertMeta />}
      hasAccess={hasAccess}
      isLoading={isLoading}
      isError={isError}
      trades={trades}
      empty={`No FZN alerts in the last ${ALERT_LOOKBACK_DAYS} sessions for ${symbol}`}
      // ⚠️ Dated rows: the window spans sessions, so "largest" ranks across
      // them and the date is the only thing distinguishing two rows.
      withDate
    />
  );
}

/* ── composition ────────────────────────────────────────────────────── */

export function Overview({ symbol }: Props): React.ReactElement {
  const { hasToolAccess } = usePermissions();
  const canSeeLevels = hasToolAccess('steamroom-levels');
  const canSeeFlow = hasToolAccess('wiseguy-alerts');
  // ⚠️ The gate stays with the data, not with the endpoint it came from. These
  // panels moved from the profile stream GEX-Ray shares onto QuantForge's
  // `/v1/contract-leaders`, but they show the same paid per-contract detail, so
  // the entitlement is unchanged. Both gates also feed `enabled:` on the
  // queries below — an unentitled reader issues no request at all.
  const canSeeContracts = hasToolAccess('gex-ray');

  // One request feeds both the Gamma Levels tiles and the chart's price lines.
  const { data: combo } = useQuery({
    queryKey: ['steamroomLevels', [symbol]],
    queryFn: () => marketDataService.fetchComboData([symbol]),
    ...EXPLORER_CACHE,
    enabled: !!symbol && canSeeLevels,
  });

  const levels = combo?.find((d) => d.symbol === symbol) ?? combo?.[0];

  // All three contract panels share this one request — they are three
  // server-ranked views of one response, not separate feeds.
  const {
    data: leaders,
    isLoading: leadersLoading,
    isError: leadersFailed,
  } = useContractLeaders(symbol, canSeeContracts);

  // Both premium panels are two rankings of one response, same as the three
  // leaderboards above — one request between them.
  const { data: top, isLoading: topLoading, isError: topFailed } = useTopContracts(
    symbol,
    canSeeContracts
  );

  // ⚠️ A 200 carrying no `session_date` is the unavailable state, and it must
  // NOT render as "no contracts" — that copy tells a reader the ticker was
  // quiet when in fact no session could be resolved at all. Folded into the
  // panels' error state, which is where "try again" belongs.
  const topUnavailable = isTopContractsUnavailable(top);

  // The Volume and Options Flow columns are deliberately NOT derived from the
  // leaderboards: they hold the top contracts per ranking, not the whole chain,
  // so summing them would give ticker totals that are quietly short. One row
  // covers both columns.
  const stats = useTickerStats(symbol, canSeeContracts);

  // Same query key as `stats` above and as the hero bar, so the Range, Volume
  // and Options Flow columns and the header are one request between them.
  const header = useSymbolHeader(symbol, canSeeContracts);

  return (
    <div className="p-3 space-y-3">
      {/* Four stat columns: two-up on a laptop, four across on a wide desk.
          One card — they are four readings of the same session, not four
          unrelated panels. */}
      <SplitCard className="grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
        <PriceStatsColumn symbol={symbol} header={header} />
        <VolumeColumn stats={stats} />
        <FlowStatsColumn symbol={symbol} stats={stats} />
        <GammaLevelsColumn symbol={symbol} levels={levels} hasAccess={canSeeLevels} />
      </SplitCard>

      <Card>
        <section className="p-4">
          {/* Keyed on the symbol: a ticker change tears the chart down and
              builds a new one, so no drawing can outlive the ticker it
              belonged to. Cheap — one series, one pane. */}
          <OverviewChart key={symbol} symbol={symbol} levels={levels?.gex_levels} />
        </section>
      </Card>

      {/* One card, two readings of the same feed: what just happened, and
          what was biggest. Separate cards would imply separate sources. Each
          table scrolls inside its own half rather than dropping columns. */}
      <SplitCard className="grid-cols-1 xl:grid-cols-2">
        <RecentFlow symbol={symbol} hasAccess={canSeeFlow} />
        <TopFlowSessions symbol={symbol} hasAccess={canSeeFlow} />
      </SplitCard>

      {/* One card. These are three rankings of a single response, so boxing
          them separately would imply three feeds where there is one.

          ⚠️ Volume is dated from `volume_session_date`. OI panels keep
          `session_date` / `prev_session_date`. Those can disagree. */}
      <SplitCard className="grid-cols-1 xl:grid-cols-3">
        <ContractLeaders
          title="Highest Volume Contracts"
          columns={['type', 'strike', 'expiry', 'volume', 'openInterest', 'impliedVolatility']}
          rows={leaders?.volume ?? null}
          asOf={leaders?.volume_session_date}
          isLoading={leadersLoading}
          isError={leadersFailed}
          hasAccess={canSeeContracts}
          symbol={symbol}
        />
        <ContractLeaders
          title="Highest OI Contracts"
          columns={['type', 'strike', 'expiry', 'openInterest', 'volume', 'impliedVolatility']}
          rows={leaders?.open_interest ?? null}
          asOf={leaders?.session_date}
          isLoading={leadersLoading}
          isError={leadersFailed}
          hasAccess={canSeeContracts}
          symbol={symbol}
        />
        <ContractLeaders
          title="Largest OI Changes"
          columns={['type', 'strike', 'expiry', 'prevOpenInterest', 'oiChange', 'openInterest']}
          rows={leaders?.oi_change ?? null}
          asOf={leaders?.session_date}
          isLoading={leadersLoading}
          isError={leadersFailed}
          hasAccess={canSeeContracts}
          symbol={symbol}
          // ⚠️ An empty oi_change list while the other two are populated is the
          // pre-settlement state — OI is known but has nothing to diff against
          // yet. It is "not yet", not "nothing changed", and emphatically not a
          // reason to render zeros.
          empty={`Open interest for ${symbol} is not yet diffable against the prior session`}
        />
      </SplitCard>

      {/* One card, two sides of one response, ranked by DIRECTIONAL net premium.
          Each row is tinted by its share of the top row ON ITS OWN SIDE, so bar
          length compares contracts within a panel and never across the two — the
          top call and the top put are both full width whatever their premiums.
          Normalising both against one maximum would flatten the quieter side
          into a row of stubs.

          ⚠️ The candidate set is the TAPE, not the alert store — every contract
          that traded is eligible. That changed at QF's #975; before it, these
          panels were alert-gated and returned one or two rows on most tickers.

          ⚠️ Ranking is `signed` (the endpoint's default), so each panel reads as
          its own direction. If this ever passes `sort="abs"`, pass the same
          value to the panels' `sortMode` — under `abs` the top row of "Top
          Calls" can be the most net-SOLD call on the tape, and only this side
          can say so. */}
      <SplitCard className="grid-cols-1 xl:grid-cols-2">
        <TopContractsPanel
          optionType="Call"
          rows={top?.calls ?? null}
          spot={top?.spot ?? null}
          sessionDate={top?.sessionDate}
          todayIso={etToday()}
          isLoading={topLoading}
          isError={topFailed || topUnavailable}
          hasAccess={canSeeContracts}
          symbol={symbol}
        />
        <TopContractsPanel
          optionType="Put"
          rows={top?.puts ?? null}
          spot={top?.spot ?? null}
          sessionDate={top?.sessionDate}
          todayIso={etToday()}
          isLoading={topLoading}
          isError={topFailed || topUnavailable}
          hasAccess={canSeeContracts}
          symbol={symbol}
        />
      </SplitCard>
    </div>
  );
}
